/**
 * Path Transition Trigger Behavior
 *
 * When the player enters this trigger, smoothly transitions them
 * to a target gameplay path at a specific T value.
 */

import { EntityType } from '../../types/EntityTypes';
import type { TriggerBehavior } from '../../types/TriggerTypes';
import { PathManager } from '../../systems/PathManager';
import { PathTransitionSystem } from '../../systems/PathTransitionSystem';
import { logger } from '../../core/Logger';

export const pathTransitionBehavior: TriggerBehavior = {
  id: 'trigger:path_transition',
  displayName: 'Path Transition',
  type: EntityType.TRIGGER_ZONE,
  category: 'trigger',
  tags: ['navigation', 'path', 'transition'],

  parameters: {
    targetPathName: {
      type: 'string',
      required: true,
      description: 'Name/ID of the target gameplay path'
    },
    targetT: {
      type: 'number',
      default: 0.0,
      min: 0,
      max: 1,
      description: 'Target T value on the path (0-1)'
    },
    maxSpeed: {
      type: 'number',
      default: 15,
      min: 1,
      max: 50,
      description: 'Maximum transition speed (m/s)'
    }
  },

  onBind: (api) => {
    const { targetPathName } = api.params;
    logger.triggers.debug(
      `Path transition trigger bound: ${api.triggerId} -> path "${targetPathName}"`
    );
  },

  onEnter: (api) => {
    const { ctx, services } = api;

    // Only trigger for player
    if (!ctx.entrant.isPlayer) {
      return;
    }

    const targetPathName = ctx.params.targetPathName as string;
    const targetT = (ctx.params.targetT as number) ?? 0;
    const maxSpeed = (ctx.params.maxSpeed as number) ?? 15;

    // Get target path
    const targetPath = PathManager.tryGetPath(targetPathName);
    if (!targetPath) {
      logger.triggers.error(
        `Path transition trigger ${ctx.self.id}: Target path not found: "${targetPathName}"`
      );
      return;
    }

    // Get source path (current player path)
    // For now, use default path - Character will track active path
    const sourcePath = PathManager.getDefaultPath();
    if (!sourcePath) {
      logger.triggers.error(
        `Path transition trigger ${ctx.self.id}: No source path available`
      );
      return;
    }

    // Don't transition if already transitioning
    if (PathTransitionSystem.isTransitioning()) {
      logger.triggers.warn(
        `Path transition trigger ${ctx.self.id}: Transition already in progress`
      );
      return;
    }

    // Get player character
    const playerEntity = services.entities.getPlayer();
    if (!playerEntity) {
      logger.triggers.error(
        `Path transition trigger ${ctx.self.id}: No player entity found`
      );
      return;
    }

    // Get Character instance from entity
    // The character should be accessible via metadata or a service
    const character = playerEntity.metadata?.characterInstance as import('../../systems/Character').Character | undefined;
    if (!character) {
      logger.triggers.error(
        `Path transition trigger ${ctx.self.id}: No character instance on player entity`
      );
      return;
    }

    logger.triggers.log(
      `🚀 Path transition: ${sourcePath.id} -> ${targetPath.id} at T=${targetT.toFixed(2)}`
    );

    // Start the transition
    PathTransitionSystem.startTransition(
      character,
      sourcePath,
      targetPath,
      targetT,
      maxSpeed,
      () => {
        logger.triggers.log(
          `✅ Path transition complete: Now on "${targetPath.id}" with mode "${targetPath.mode}"`
        );
      }
    );
  }
};
